/**
 * Заявки на доступ и как пригласить (§8.3 ТЗ платформы, D187).
 *
 * Заявка у человека одна на все три бота: в ней — все агенты, куда он
 * просится. Владелец назначает должность, уровень и отмечает агенты.
 * Приглашения ссылкой-кодом в системе нет (см. `GET /api/platform/invite`):
 * новый сотрудник пишет любому боту — отсюда ссылки на ботов.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { formatShortDateTime } from '@shell/format';
import { Empty, ErrorState, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { Icon } from '../../icons';
import { Notice } from '../../ui';
import { useMayEditTeam } from './access';
import { botLinks, LEVEL_NAMES, SERVICE_NAMES, SERVICE_ORDER, problemText, teamApi } from './api';
import type { AccessRequest, PositionOption, UsersScreen } from './api';

export function Requests({
  users,
  bots,
}: {
  users: UseQueryResult<UsersScreen, ApiError>;
  bots?: Navigation['bots'];
}) {
  return (
    <div className="team-requests">
      <Invite bots={bots} />
      {users.isPending ? (
        <Loading text="Загружаю заявки…" />
      ) : users.error ? (
        <ErrorState error={users.error} onRetry={() => users.refetch()} />
      ) : (
        <section className="team-block" aria-label="Заявки">
          <h3 className="block-title">Ждут решения · {users.data.requests.length}</h3>
          {users.data.requests.length === 0 ? (
            <Empty title="Новых заявок нет" text="Заявка появится, когда человек напишет боту." />
          ) : (
            <ul className="team-request-list">
              {users.data.requests.map((request) => (
                <RequestCard key={request.id} request={request} positions={users.data.positions} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function Invite({ bots }: { bots?: Navigation['bots'] }) {
  const invite = useQuery<{ how: string }, ApiError>({
    queryKey: ['team', 'invite'],
    queryFn: () => teamApi.invite(),
    staleTime: Infinity,
  });
  const links = botLinks(bots);
  return (
    <section className="team-block team-block--card">
      <h3 className="block-title">Как пригласить</h3>
      <p className="team-hint">
        {invite.data?.how ??
          'Новый сотрудник открывает любого из ботов, нажимает «Запросить доступ» и заполняет короткую анкету.'}
      </p>
      <div className="team-actions">
        {links.map((bot) => (
          <a key={bot.service} className="ghost-button team-bot" href={bot.href} target="_blank" rel="noreferrer">
            {bot.title} <Icon name="external" size={14} />
          </a>
        ))}
      </div>
    </section>
  );
}

function requested(request: AccessRequest): string[] {
  return request.services?.length ? request.services : [request.service];
}

function RequestCard({
  request,
  positions,
}: {
  request: AccessRequest;
  positions: PositionOption[];
}) {
  const mayEdit = useMayEditTeam();
  const client = useQueryClient();
  const candidates = request.candidates ?? [];
  const exact = candidates.filter((candidate) => candidate.match === 'exact');
  // Сразу выбран только единственный полный совпавший: при двух «Алинах»
  // решает владелец, а не порядок в списке (D234).
  const [employeeId, setEmployeeId] = useState(
    exact.length === 1 ? String(exact[0].employee_id) : '',
  );
  const [positionId, setPositionId] = useState('');
  const [level, setLevel] = useState('user');
  const [chosen, setChosen] = useState<string[]>(requested(request));
  const [error, setError] = useState<string | null>(null);

  const done = () => void client.invalidateQueries({ queryKey: ['team'] });
  const approve = useMutation<unknown, ApiError>({
    mutationFn: () =>
      teamApi.approve(request.id, {
        services: chosen,
        access_level: level,
        position_id: positionId ? Number(positionId) : null,
        employee_id: employeeId ? Number(employeeId) : null,
      }),
    onSuccess: done,
    onError: (failure) => setError(problemText(failure)),
  });
  const reject = useMutation<unknown, ApiError>({
    mutationFn: () => teamApi.reject(request.id),
    onSuccess: done,
    onError: (failure) =>
      setError(
        failure.status === 400 && !failure.detail
          ? 'Заявку уже рассмотрели. Обновите страницу.'
          : problemText(failure),
      ),
  });
  const busy = approve.isPending || reject.isPending || !mayEdit;
  const picked = candidates.find((candidate) => String(candidate.employee_id) === employeeId);

  return (
    <li className="team-request">
      <div className="team-request__head">
        <strong>{request.applicant_name ?? 'Без имени'}</strong>
        <span className="team-faint">{formatShortDateTime(request.requested_at)}</span>
      </div>
      <p className="team-hint">
        Просит: {requested(request).map((service) => SERVICE_NAMES[service] ?? service).join(', ')}
      </p>
      {request.applicant_note && <p className="team-request__note">{request.applicant_note}</p>}

      {candidates.length > 0 && (
        <div className="team-request__who">
          <label className="field">
            <span className="field__label">Кто это</span>
            <select
              className="select"
              value={employeeId}
              disabled={!mayEdit}
              onChange={(event) => setEmployeeId(event.target.value)}
            >
              <option value="">Новый сотрудник — завести из анкеты</option>
              {candidates.some((c) => c.match === 'exact') && (
                <optgroup label="Совпадает имя">
                  {candidates
                    .filter((c) => c.match === 'exact')
                    .map((c) => (
                      <option key={c.employee_id} value={c.employee_id}>
                        {c.full_name}
                        {c.position_name ? ` — ${c.position_name}` : ''}
                      </option>
                    ))}
                </optgroup>
              )}
              {candidates.some((c) => c.match === 'partial') && (
                <optgroup label="Похожие">
                  {candidates
                    .filter((c) => c.match === 'partial')
                    .map((c) => (
                      <option key={c.employee_id} value={c.employee_id}>
                        {c.full_name}
                        {c.position_name ? ` — ${c.position_name}` : ''}
                      </option>
                    ))}
                </optgroup>
              )}
              {candidates.some((c) => c.match === 'none') && (
                <optgroup label="Остальные без Telegram">
                  {candidates
                    .filter((c) => c.match === 'none')
                    .map((c) => (
                      <option key={c.employee_id} value={c.employee_id}>
                        {c.full_name}
                        {c.position_name ? ` — ${c.position_name}` : ''}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
          </label>
          <p className="team-hint">
            {picked
              ? `Telegram привяжется к «${picked.full_name}» из справочника — его смены и часы останутся при нём.`
              : exact.length > 1
                ? 'Совпадает несколько человек — выберите нужного.'
                : 'Если человек уже есть в справочнике (например, внесён для табеля), выберите его — иначе появится второй.'}
          </p>
        </div>
      )}

      <div className="team-form">
        <label className="field">
          <span className="field__label">Должность</span>
          <select
            className="select"
            value={positionId}
            disabled={!mayEdit}
            onChange={(event) => setPositionId(event.target.value)}
          >
            <option value="">
              {picked
                ? `оставить как в справочнике${picked.position_name ? ` (${picked.position_name})` : ''}`
                : 'не назначать'}
            </option>
            {positions.map((position) => (
              <option key={position.id} value={position.id}>
                {position.is_owner ? `${position.name} — все права` : position.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Уровень</span>
          <select
            className="select"
            value={level}
            disabled={!mayEdit}
            onChange={(event) => setLevel(event.target.value)}
          >
            <option value="user">{LEVEL_NAMES.user}</option>
            <option value="admin">{LEVEL_NAMES.admin}</option>
          </select>
        </label>
      </div>
      <fieldset className="team-services">
        <legend className="field__label">Открыть вход</legend>
        {SERVICE_ORDER.map((service) => (
          <label key={service} className="team-check">
            <input
              type="checkbox"
              checked={chosen.includes(service)}
              disabled={!mayEdit}
              onChange={() =>
                setChosen((current) =>
                  current.includes(service)
                    ? current.filter((item) => item !== service)
                    : [...current, service],
                )
              }
            />
            {SERVICE_NAMES[service]}
          </label>
        ))}
      </fieldset>
      {error && <Notice kind="error">{error}</Notice>}
      <div className="team-actions">
        <button
          type="button"
          className="button button--primary"
          disabled={busy || chosen.length === 0}
          onClick={() => {
            setError(null);
            approve.mutate();
          }}
        >
          Одобрить
        </button>
        <button
          type="button"
          className="button button--danger"
          disabled={busy}
          onClick={() => {
            setError(null);
            reject.mutate();
          }}
        >
          Отклонить
        </button>
      </div>
    </li>
  );
}
